import { useState, useRef, useCallback } from 'react'
import { getProductFields, putProductFields } from './productFieldsApi'
import { toDraft, changedFields, fieldProblems, describeWriteFailure } from './productFieldsForm'
import { titleAfterSave, TITLE_FIELDS } from './productTitle'

// The scanned piece's product fields: what Shopify holds (`baseline`), what the
// clerk has typed (`draft`), and the save. `blocking` is what the Upload
// button reads: a loaded piece with a required field empty, or a value the
// server would refuse, cannot be uploaded. A piece whose fields could not be
// loaded is not blocked -- the photos are not lost, and publishing still
// waits on the required fields server side.
export default function useProductFields(fetchResource) {
    const [status, setStatus] = useState('idle')
    const [product, setProduct] = useState(null)
    const [baseline, setBaseline] = useState({})
    const [draft, setDraft] = useState({})
    const [loadError, setLoadError] = useState(null)
    const [saving, setSaving] = useState(false)
    const [saveResult, setSaveResult] = useState(null)
    // The sku on screen. A response for any other (a cancelled item) is dropped.
    const skuRef = useRef(null)

    function apply(response) {
        const fresh = toDraft(response.fields)
        setProduct(response)
        setBaseline(fresh)
        setDraft(fresh)
    }

    const reset = useCallback(() => {
        skuRef.current = null
        setStatus('idle')
        setProduct(null)
        setBaseline({})
        setDraft({})
        setLoadError(null)
        setSaveResult(null)
    }, [])

    async function load(sku) {
        skuRef.current = sku
        setStatus('loading')
        setLoadError(null)
        setSaveResult(null)
        const response = await getProductFields(fetchResource, sku)
        if (skuRef.current !== sku) return
        if (response.error || !response.fields) {
            setLoadError(response.error || null)
            setStatus('failed')
            return
        }
        apply(response)
        setStatus('loaded')
    }

    function setField(key, value) {
        setSaveResult(null)
        setDraft(prev => ({ ...prev, [key]: value }))
    }

    const problems = status === 'loaded' ? fieldProblems(draft, product.required, baseline) : {}
    const changes = status === 'loaded' ? changedFields(draft, baseline) : {}
    const hasChanges = Object.keys(changes).length > 0
    const blocking = Object.keys(problems).length > 0

    // Resolves true when there is nothing left to save. The inputs stay live
    // while the PUT runs, so the answer is merged key by key: a field the clerk
    // changed again since the save started keeps the newer value (and stays
    // unsaved); every other field takes what the server now holds.
    async function save() {
        if (!hasChanges) return true
        const sku = skuRef.current
        const sent = draft
        const title = titleAfterSave(product, sent, baseline)
        setSaving(true)
        const response = await putProductFields(fetchResource, sku, changes)
        setSaving(false)
        if (skuRef.current !== sku) return false
        if (response.error) {
            const partial = describeWriteFailure(response)
            if (partial) {
                // The landed ones are on the product now: only the rest stays
                // unsaved. The server rebuilds the title only from a save that
                // carries a title field, so when the title did not land, this
                // save's title fields stay unsaved too and the next Save sends
                // them again. `title` itself is not a form field.
                const titleMissed = !partial.landed.includes('title')
                    && [...partial.failed, ...partial.notAttempted].includes('title')
                const settled = partial.landed.filter(key => Object.hasOwn(sent, key) && !(titleMissed && TITLE_FIELDS.includes(key)))
                setBaseline(prev => ({ ...prev, ...Object.fromEntries(settled.map(key => [key, sent[key]])) }))
                if (partial.landed.includes('title')) setProduct(prev => ({ ...prev, title }))
            }
            setSaveResult({ type: 'error', error: response.error, partial })
            return false
        }
        const fresh = toDraft(response.fields)
        setProduct(response)
        setBaseline(fresh)
        setDraft(current => Object.fromEntries(Object.keys(fresh).map(key => [
            key,
            JSON.stringify(current[key]) === JSON.stringify(sent[key]) ? fresh[key] : current[key],
        ])))
        setSaveResult({ type: 'success' })
        return true
    }

    return { status, product, draft, baseline, loadError, saving, saveResult, problems, hasChanges, blocking, load, reset, setField, save }
}
